import React, { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { VideoSectionProps, VideoSectionRef, VideoSectionVideoState } from './VideoSection.types'
import styles from './VideoSection.module.scss'
import cl from 'classnames'
import { LabeledLine } from '../LabeledLine'
import { Text1 } from '../Text1'
import { Input } from '../Input'
import { Button } from '../Button'
import { IconCopy, IconUpload } from 'assets'
import { Text2 } from '../Text2'
import { useDropzone } from 'react-dropzone'
import { VideoPlayer } from './VideoPlayer'
import { Cloud, FileStatus } from 'utilities'
import { handleVideoUpload, validateVideoAspectRatio } from 'utilities/Gcp'

const MAX_FILE_SIZE_MB = 20000; // 20000 MB (20GB)
const VideoSectionComponent = (props: VideoSectionProps, ref: Ref<VideoSectionRef>) => {
  const {
    videoUrl,
    triggerNewVideoProcessing,
    readyForEdit,
    openInEditor,
    onChange
  } = props

  const [video, setVideo] = useState<VideoSectionVideoState>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState(readyForEdit);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedManageVideoStatus = localStorage.getItem('manageVideoStatus');
      const storedReadyForEdit = JSON.parse(localStorage.getItem('readyForEdit') || 'false');

      setProcessingStatus(storedReadyForEdit);

      if (storedReadyForEdit) {
        setVideo((s) => ({ ...s, status: FileStatus.UPLOADED }));
      } else {
        if (storedManageVideoStatus === 'PROCESSING') {
          setVideo((s) => ({ ...s, status: FileStatus.PROCESSING }));
        } else if (storedManageVideoStatus === 'COMPLETED') {
          setProcessingStatus(true);
          setVideo((s) => ({ ...s, status: FileStatus.UPLOADED }));
        } else {
          setProcessingStatus(false);
          setVideo((s) => ({ ...s, status: undefined }));
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    if (!videoUrl) return;

    const storedManageVideoStatus = localStorage.getItem('manageVideoStatus');
    const storedReadyForEdit = JSON.parse(localStorage.getItem('readyForEdit') || 'false');
    setVideo({
      src: videoUrl,
      status: FileStatus.UPLOADED
    })
    if (storedReadyForEdit) {
      setProcessingStatus(true);
      setVideo((s) => ({ ...s, status: FileStatus.UPLOADED }));
    } else {
      if (storedManageVideoStatus === 'PROCESSING') {
        setProcessingStatus(false);
        setVideo((s) => ({ ...s, status: FileStatus.PROCESSING }));
      } else if (storedManageVideoStatus === 'COMPLETED') {
        setProcessingStatus(true);
      } else {
        setProcessingStatus(false);
      }
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      setVideo(null) // for cleanup the video state
    }
  }, [videoUrl])

  useEffect(() => {
    const interval = setInterval(() => {
      const status = localStorage.getItem('manageVideoStatus')
      if (status === 'COMPLETED') {
        setProcessingStatus(true)
        setVideo((s) => ({ ...s, status: FileStatus.UPLOADED }))
      }
    }, 500)

    return () => clearInterval(interval)
  }, [])

  useImperativeHandle(ref, () => ({
    async handleVideo () {
      if (!video || video.status !== FileStatus.LOADED) {
        return video?.src as string;
      }

      setVideo(s => ({ ...s, status: FileStatus.UPLOADING }));
      setUploadProgress(0);

      try {
        const response = await handleVideoUpload(video.file, (progress) => {
          setUploadProgress(progress)
        });

        setVideo((s) => ({
          ...s,
          src: response.location,
          status: FileStatus.UPLOADED
        }));
        return response.location;
      } catch (error) {
        console.error('Upload failed:', error);
        setVideo((s) => ({ ...s, status: FileStatus.ERROR }));
      } finally {
        setUploadProgress(0);
      }
    },
    getVideoUrl () {
      return video?.src as string || '';
    }
  }));

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setErrorMessage(null);
    const [currentFile] = acceptedFiles;

    const fileSizeInMB = currentFile.size / (1024 * 1024);
    if (fileSizeInMB > MAX_FILE_SIZE_MB) {
      setErrorMessage(`File size exceeds the limit of ${MAX_FILE_SIZE_MB} MB.`);
      setUploadProgress(0);
      return;
    }

    setVideo((s) => ({ ...s, status: FileStatus.LOADING }));
    setIsVideoLoading(true);

    try {
      // Validate aspect ratio before loading the video
      const validationResult = await validateVideoAspectRatio(currentFile)

      if (!validationResult.valid) {
        setErrorMessage(validationResult.message || 'Invalid video aspect ratio.')
        setVideo(null)
        setIsVideoLoading(false)
        return
      }

      const videoUrl = URL.createObjectURL(currentFile)

      const videoDetails: VideoSectionVideoState = {
        file: currentFile,
        src: videoUrl,
        status: FileStatus.LOADED,
        contentType: currentFile.type
      };

      setVideo(videoDetails);
      setIsVideoLoading(false);
      onChange && onChange();
    } catch (error) {
      console.error('Error loading video file:', error);
      setErrorMessage('Failed to load the video file.');
      setVideo((s) => ({ ...s, status: FileStatus.ERROR }));
      setIsVideoLoading(false);
    }
  }, [onChange]);

  const handleTriggerVideoProcessing = async () => {
    setVideo((s) => ({ ...s, status: FileStatus.PROCESSING }));
    try {
      await triggerNewVideoProcessing();
      setVideo((s) => ({ ...s, status: FileStatus.UPLOADED }));
      setProcessingStatus(true);
    } catch (e) {
      setVideo((s) => ({ ...s, status: undefined }));
      console.error(e);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ['video/mp4', 'video/quicktime', 'video/webm', '.mov', '.mp4', '.webm']
  })
  const {
    onClick,
    ...rootProps
  } = getRootProps()

  const copyVideoUrlToClipboard = async () => {
    if (!videoUrl || typeof videoUrl !== 'string') return
    try {
      await navigator.clipboard.writeText(videoUrl)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <LabeledLine
      label='Video'
      alignItems='flexStart'
      className={styles.container}
      innerContainerProps={{
        className: styles.innerContainer
      }}
    >
      {errorMessage && (
        <Text1 style={{ color: styles.error, marginBottom: 10 }}>
          {errorMessage}
        </Text1>
      )}
      {(video && ![FileStatus.LOADING, FileStatus.LOADED].includes(video.status))
        ? (
          <div className={styles.statusContainer}>
            <div>
              <Text1>Ready for edit:</Text1>
              {processingStatus
                ? <Text1 style={{ color: styles.primary }}>Yes</Text1>
                : <Text1 style={{ color: styles.error }}>No</Text1>
              }
              {video?.status === FileStatus.UPLOADING && (
                <Text1 style={{ color: styles.primary, marginTop: '10px' }}>
                Uploading...
                </Text1>
              )}
              {video?.status === FileStatus.PROCESSING && (
                <Text1 style={{ color: styles.primary, marginTop: '10px' }}>
                Video processing, please wait...
                </Text1>
              )}
            </div>
            <Button
              preset='primary'
              style={{ width: 175, margin: '0px 0px 10px 0px' }}
              disabled={video?.status === FileStatus.PROCESSING || video?.status === FileStatus.UPLOADING}
              onClick={processingStatus ? openInEditor : handleTriggerVideoProcessing}
            >
              {video?.status === FileStatus.PROCESSING
                ? 'Processing...'
                : processingStatus
                  ? 'Open in Editor'
                  : 'Run process again'
              }
            </Button>
          </div>
        )
        : (
          <Text1 style={{ color: styles.placeholderContent }}>
            No video uploaded yet.
          </Text1>
        )}

      <div
        className={cl(styles.dropZone, { [styles.dropZoneActive]: isDragActive })}
        {...rootProps}
      >
        <input {...getInputProps()} />
        {video
          ? <VideoPlayer src={video.src} key={video.src as string} />
          : (
            <>
              <IconUpload fill={styles.placeholderContent} />
              <Text2 className={styles.placeholderText}>
                Drag & Drop a video file here or use Browse files button
                <p style={{ color: 'red' }}> The file type must be MP4, MOV, WebM with H.264 codec</p>
              </Text2>
              <Button
                preset='secondary'
                onClick={onClick}
              >
                Browse files
              </Button>
            </>
          )}
      </div>
      <div style={{ fontSize: 12, color: styles.secondaryContent, padding: '10px 0px' }}>Supported aspect ratios: 16:9, 40:27, 427:240.</div>

      <div className={styles.urlContainer}>
        <Text1>
          URL
        </Text1>
        {videoUrl
          ? (
            <a
              href={videoUrl}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.link}
            >
              <Text1 overflowEllipsis>
                {videoUrl}
              </Text1>
            </a>
          )
          : (
            <Input
              placeholder='Add URL...'
            />
          )}
        <Button
          preset='icon'
          title='Copy'
          onClick={copyVideoUrlToClipboard}
        >
          <IconCopy fill={styles.secondaryContent} />
        </Button>
      </div>

      <div className={styles.progressBarContainer}>
        {uploadProgress > 0 && (
          <>
            <div className={styles.progressBar} style={{ width: `${uploadProgress}%`, marginBottom: '10px' }} />
          </>
        )}
      </div>

    </LabeledLine>
  )
}

const VideoSection = forwardRef<VideoSectionRef, VideoSectionProps>(VideoSectionComponent)
export { VideoSection }
